import { useMemo } from "react";
import { useMetrics } from "hooks/useMetrics";
import MetricsExtractor from "logic/metrics/MetricsExtractor";
const METRIC_PREFIX = "org.graylog.plugins.datalake.active";
const STREAM_METRIC_PREFIX = `${METRIC_PREFIX}.stream`;
const aggregateMetricNames = {
  totalBytes: `${METRIC_PREFIX}.total-files-size-bytes`,
  totalRecords: `${METRIC_PREFIX}.total-records`,
  totalDataFiles: `${METRIC_PREFIX}.total-data-files`,
  totalStreams: `${METRIC_PREFIX}.total-streams`
};
const aggregateMetricValues = Object.values(aggregateMetricNames);
const buildStreamMetricNames = (streamId) => ({
  bytes: `${STREAM_METRIC_PREFIX}.${streamId}.total-files-size-bytes`,
  records: `${STREAM_METRIC_PREFIX}.${streamId}.total-records`,
  files: `${STREAM_METRIC_PREFIX}.${streamId}.total-data-files`
});
const getMetricValueForNode = (nodeMetrics, metricName) => {
  if (!nodeMetrics) {
    return void 0;
  }
  const { value } = MetricsExtractor.getValuesForNode(nodeMetrics, { value: metricName });
  return typeof value === "number" && Number.isFinite(value) ? value : void 0;
};
const getFirstNonZeroMetricValue = (metricsByNode, metricName) => {
  var _a;
  return (_a = Object.keys(metricsByNode != null ? metricsByNode : {}).map((nodeId) => getMetricValueForNode(metricsByNode == null ? void 0 : metricsByNode[nodeId], metricName)).find((value) => value !== void 0 && value !== 0)) != null ? _a : 0;
};
export const useDataLakeIcebergAggregateMetrics = () => {
  const { data: metricsByNode, isLoading } = useMetrics(aggregateMetricValues);
  return useMemo(
    () => ({
      totalBytes: getFirstNonZeroMetricValue(metricsByNode, aggregateMetricNames.totalBytes),
      totalRecords: getFirstNonZeroMetricValue(metricsByNode, aggregateMetricNames.totalRecords),
      totalDataFiles: getFirstNonZeroMetricValue(metricsByNode, aggregateMetricNames.totalDataFiles),
      totalStreams: getFirstNonZeroMetricValue(metricsByNode, aggregateMetricNames.totalStreams),
      isLoading
    }),
    [isLoading, metricsByNode]
  );
};
export const useDataLakeIcebergStreamMetrics = (streamIds) => {
  const uniqueStreamIds = useMemo(() => [...new Set(streamIds.filter(Boolean))].sort(), [streamIds]);
  const metricNames = useMemo(
    () => uniqueStreamIds.flatMap((streamId) => Object.values(buildStreamMetricNames(streamId))),
    [uniqueStreamIds]
  );
  const { data: metricsByNode, isLoading } = useMetrics(metricNames);
  const metricsByStreamId = useMemo(
    () => new Map(
      uniqueStreamIds.map((streamId) => {
        const streamMetricNames = buildStreamMetricNames(streamId);
        const streamMetrics = {
          bytes: getFirstNonZeroMetricValue(metricsByNode, streamMetricNames.bytes),
          records: getFirstNonZeroMetricValue(metricsByNode, streamMetricNames.records),
          files: getFirstNonZeroMetricValue(metricsByNode, streamMetricNames.files)
        };
        return [streamId, streamMetrics];
      })
    ),
    [metricsByNode, uniqueStreamIds]
  );
  return { metricsByStreamId, isLoading };
};
